/** Biblioteca de diseños: planos inteligentes guardados como plantillas reutilizables.
 *  Compartida por el equipo SA. Aplicar un diseño NUNCA confirma solo: queda pendiente de revisión. */
import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { PlanoSVG } from './PlanoInteligente';

export const BibliotecaPlanos = ({ planoActual, hayPlano, user, onUsar, onClose }) => {
  const [lista, setLista] = useState(null);
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [confirmUso, setConfirmUso] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const cargar = () => api.get('/sa/pizarra/biblioteca').then((r) => setLista(r.data.disenos || [])).catch(() => setLista([]));
  useEffect(() => { cargar(); }, []);

  const guardar = async () => {
    if (!nombre.trim()) return toast.error('Ponle un nombre al diseño');
    setGuardando(true);
    try {
      await api.post('/sa/pizarra/biblioteca', { nombre: nombre.trim(), plano: planoActual });
      toast.success(`Diseño «${nombre.trim()}» guardado en la biblioteca`);
      setNombre('');
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); }
    setGuardando(false);
  };
  const borrar = async (id) => {
    try {
      await api.delete(`/sa/pizarra/biblioteca/${id}`);
      setLista((l) => l.filter((d) => d.id !== id));
      toast.success('Diseño eliminado de la biblioteca');
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo eliminar'); }
    setConfirmDel(null);
  };
  const puedeBorrar = (d) => (user?.role || '') === 'admin' || d.vendor_id === user?.id;

  return (
    <div className="fixed inset-0 z-50" data-testid="pizarra-biblioteca">
      <div className="absolute inset-0 bg-black/35" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 lg:inset-x-auto lg:right-4 lg:top-16 lg:bottom-4 lg:w-[420px] bg-white rounded-t-[18px] lg:rounded-[18px] shadow-2xl max-h-[82vh] lg:max-h-none overflow-y-auto p-3 space-y-3">
        <div className="flex justify-between items-center">
          <p className="text-xs font-black text-[#38205F]">📚 Biblioteca de diseños</p>
          <button onClick={onClose} className="text-[#6B6B6B] font-black px-2 py-1" data-testid="biblioteca-cerrar">✕</button>
        </div>
        {planoActual ? (
          <div className="border border-[#D5AD65]/70 bg-[#FDF8EE] rounded-[12px] p-2.5 space-y-1.5">
            <p className="text-[10px] font-black uppercase text-[#38205F]">💾 Guardar el plano actual como plantilla</p>
            <div className="flex gap-1.5">
              <input value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={60}
                onKeyDown={(e) => e.key === 'Enter' && guardar()} placeholder="Ej. «Mampara esquina puerta + fijo»"
                className="flex-1 h-9 px-2.5 text-[12px] rounded-lg border border-[#B9A5D9]/60 outline-none focus:border-[#7352B5]" data-testid="biblioteca-nombre" />
              <button onClick={guardar} disabled={guardando}
                className="text-[11px] font-black text-white bg-[#38205F] rounded-lg px-3 disabled:opacity-60" data-testid="biblioteca-guardar">
                {guardando ? '…' : 'Guardar'}
              </button>
            </div>
            <p className="text-[9px] text-[#6B6B6B]">Se guarda solo la geometría (paneles, medidas, herrajes) — sin productos ni precios.</p>
          </div>
        ) : (
          <p className="text-[10px] text-[#6B6B6B] border border-[#B9A5D9]/50 rounded-[10px] p-2">Esta pizarra aún no tiene plano inteligente. Genera uno con ✨ o aplica un diseño guardado.</p>
        )}
        <div className="space-y-2">
          <p className="text-[10px] font-black uppercase text-[#6B6B6B]">Diseños guardados por el equipo</p>
          {lista === null && <p className="text-[11px] text-[#6B6B6B]">Cargando…</p>}
          {lista && lista.length === 0 && <p className="text-[11px] text-[#6B6B6B] italic">Aún no hay diseños en la biblioteca.</p>}
          {(lista || []).map((d) => (
            <div key={d.id} className="border border-[#B9A5D9]/60 rounded-[12px] p-2 space-y-1.5" data-testid={`biblioteca-item-${d.id}`}>
              <div className="pointer-events-none border border-[#F4EFFB] rounded-[10px] overflow-hidden">
                <PlanoSVG plano={d.plano} />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="text-[11px] font-black text-[#1F1B29] block truncate">{d.nombre}</span>
                  <span className="text-[9px] text-[#6B6B6B]">{(d.plano?.paneles || []).length} panel(es) · {d.creado_por}</span>
                </span>
                <span className="flex gap-1.5 shrink-0">
                  {confirmUso === d.id ? (
                    <>
                      <button onClick={() => onUsar(d.plano, d.nombre)} className="text-[10px] font-black text-white bg-amber-500 rounded-lg px-2.5 py-1.5" data-testid={`biblioteca-usar-si-${d.id}`}>Sí, reemplazar</button>
                      <button onClick={() => setConfirmUso(null)} className="text-[10px] font-bold text-[#6B6B6B] border border-[#B9A5D9]/60 rounded-lg px-2 py-1.5" data-testid={`biblioteca-usar-no-${d.id}`}>No</button>
                    </>
                  ) : (
                    <button onClick={() => (hayPlano ? setConfirmUso(d.id) : onUsar(d.plano, d.nombre))}
                      className="text-[10px] font-black text-white bg-[#7352B5] rounded-lg px-3 py-1.5" data-testid={`biblioteca-usar-${d.id}`}>✓ Usar</button>
                  )}
                  {puedeBorrar(d) && (confirmDel === d.id ? (
                    <>
                      <button onClick={() => borrar(d.id)} className="text-[10px] font-black text-white bg-red-500 rounded-lg px-2 py-1.5" data-testid={`biblioteca-borrar-si-${d.id}`}>Sí</button>
                      <button onClick={() => setConfirmDel(null)} className="text-[10px] font-bold text-[#6B6B6B] border border-[#B9A5D9]/60 rounded-lg px-2 py-1.5" data-testid={`biblioteca-borrar-no-${d.id}`}>No</button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmDel(d.id)} className="text-[10px] font-bold text-red-400 border border-red-200 rounded-lg px-2 py-1.5" data-testid={`biblioteca-borrar-${d.id}`}>🗑</button>
                  ))}
                </span>
              </div>
              {confirmUso === d.id && <p className="text-[9px] text-amber-700">Reemplaza el plano actual de esta pizarra (las correcciones hechas al plano se pierden; marcadores y productos NO cambian).</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
